import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../shared/DatePicker';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import {
  dateFromIso,
  dayOfMonth,
  formatClock12,
  formatDate,
  formatHM,
  formatLongDate,
  isWeekend,
  isoFromDate,
  weekdayShort,
} from '../../utils/timesheet';
import QuickAdd from './QuickAdd';
import PeriodActionModal from './PeriodActionModal';

// What a full day is measured against for the bar under each day's total.
const FULL_DAY_SECONDS = 8 * 3600;
// A press has to travel this far to be a drag, so a click still opens the entry.
const DRAG_DISTANCE = 6;
const MENU_WIDTH = 190;
const MENU_HEIGHT = 176;

const startOrder = (a, b) => String(a.startTime || '99').localeCompare(String(b.startTime || '99'));

// One entry's card. Left click opens it, right click its menu, and a left drag moves it.
function EntryCard({ entry, taskById, movable, onOpen, onMenu, overlay = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: entry.id, data: { entry }, disabled: !movable || overlay });
  const task = taskById.get(entry.taskId);
  const root = task ? taskById.get(task.rootId) : null;
  const times = entry.startTime && entry.endTime ? `${formatClock12(entry.startTime)} – ${formatClock12(entry.endTime)}` : null;
  const classes = ['ts-week__item', movable ? 'is-movable' : '', isDragging ? 'is-dragging' : '', overlay ? 'is-overlay' : ''].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      ref={overlay ? undefined : setNodeRef}
      data-entry-id={overlay ? undefined : entry.id}
      className={classes}
      style={{ '--ts-chip': task?.color || root?.color || '#75777d' }}
      onClick={() => onOpen?.(entry)}
      onContextMenu={(e) => onMenu?.(e, entry)}
      title={[times, entry.description].filter(Boolean).join('\n') || undefined}
      {...(overlay ? {} : attributes)}
      {...(overlay ? {} : listeners)}
    >
      {root && root.id !== task?.id ? <span className="ts-week__project">{root.name}</span> : null}
      <span className="ts-week__task">{task?.name || 'Unknown task'}</span>
      <span className="ts-week__hours">{formatHM(entry.durationSeconds)}</span>
    </button>
  );
}

// The menu a right click opens, at the pointer and kept on screen.
function EntryMenu({ x, y, canChange, onPick, onClose }) {
  const menuRef = useRef(null);
  const items = [
    { key: 'edit', label: 'Edit Entry', icon: 'bi-pencil', enabled: true },
    { key: 'move', label: 'Move Entry', icon: 'bi-arrow-left-right', enabled: canChange },
    { key: 'clone', label: 'Clone Entry', icon: 'bi-files', enabled: canChange },
    { key: 'delete', label: 'Delete Entry', icon: 'bi-trash', enabled: canChange, danger: true },
  ];

  useEffect(() => {
    menuRef.current?.querySelector('button:not(:disabled)')?.focus();
    const away = (e) => {
      if (!menuRef.current?.contains(e.target)) onClose();
    };
    const leave = () => onClose();
    document.addEventListener('pointerdown', away, true);
    window.addEventListener('blur', leave);
    window.addEventListener('resize', leave);
    window.addEventListener('scroll', leave, true);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      window.removeEventListener('blur', leave);
      window.removeEventListener('resize', leave);
      window.removeEventListener('scroll', leave, true);
    };
  }, [onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const buttons = [...menuRef.current.querySelectorAll('button:not(:disabled)')];
    const at = buttons.indexOf(document.activeElement);
    buttons[(at + (e.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length]?.focus();
  };

  return createPortal(
    <div
      ref={menuRef}
      className="ts-week__menu"
      role="menu"
      aria-label="Entry actions"
      style={{
        left: Math.max(8, Math.min(x, window.innerWidth - MENU_WIDTH - 8)),
        top: Math.max(8, Math.min(y, window.innerHeight - MENU_HEIGHT - 8)),
      }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={`ts-week__menuitem${item.danger ? ' is-danger' : ''}`}
          disabled={!item.enabled}
          onClick={() => onPick(item.key)}
        >
          <i className={`bi ${item.icon}`} aria-hidden="true" />
          {item.label}
        </button>
      ))}
    </div>,
    document.body
  );
}

function DayColumn({ date, today, entries, taskById, editable, flag, quickOpen, onQuick, onOpen, onMenu, onDayClick }) {
  const { setNodeRef, isOver, active } = useDroppable({ id: date, disabled: !editable });
  const seconds = entries.reduce((s, e) => s + e.durationSeconds, 0);
  const weekend = isWeekend(date);
  const empty = !weekend && seconds === 0 && date < today;
  const classes = [
    'ts-week__day',
    weekend ? 'is-weekend' : '',
    date === today ? 'is-today' : '',
    flag || empty ? 'is-flagged' : '',
    isOver && active?.data.current?.entry?.date !== date ? 'is-droptarget' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={setNodeRef} className={classes} role="listitem">
      <button type="button" className="ts-week__head" onClick={() => onDayClick(date)} aria-label={`Open ${formatLongDate(date)}`}>
        <span className="ts-week__dow">{weekdayShort(date)}</span>
        <span className="ts-week__dom">{dayOfMonth(date)}</span>
      </button>
      <div className="ts-week__total">
        <span>{seconds ? formatHM(seconds) : '—'}</span>
        <span className="ts-week__bar" aria-hidden="true">
          <span style={{ width: `${Math.min(100, (seconds / FULL_DAY_SECONDS) * 100)}%` }} />
        </span>
        {flag ? (
          <span className="ts-week__note">{flag.count} check{flag.count === 1 ? '' : 's'} flagged</span>
        ) : empty ? (
          <span className="ts-week__note">Nothing logged</span>
        ) : null}
      </div>

      {[...entries].sort(startOrder).map((entry) => (
        <EntryCard
          key={entry.id}
          entry={entry}
          taskById={taskById}
          movable={editable && !entry.locked}
          onOpen={onOpen}
          onMenu={onMenu}
        />
      ))}

      {editable ? (
        <div className="ts-week__foot">
          <button type="button" className="ts-week__log" aria-expanded={quickOpen} onClick={(e) => onQuick(date, e.currentTarget)}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Log time
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The week as a column per day: the day's total and a bar toward a full day,
 * then one card per entry. A card opens its entry; a right click gives its menu
 * (edit, move, clone, delete); dragging it to another day moves it. Log time
 * floats a quick add out from its button. The day heading opens the day view.
 *
 * Locked entries (a submitted period, approved time off) stay put: they open
 * read-only, and their menu offers nothing that would change them.
 */
export default function WeekColumns({
  days,
  today,
  entries,
  tasks,
  taskById,
  config,
  editable,
  onOpen,
  onMove,
  onClone,
  onDelete,
  onQuickAdd,
  onDayClick,
  dayFlags = null,
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: DRAG_DISTANCE } }));
  const [dragging, setDragging] = useState(null);
  const [menu, setMenu] = useState(null); // { x, y, entry, from }
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDate, setMoveDate] = useState(null);
  const [moving, setMoving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const moveShown = useLastPresent(moveTarget);
  const deleteShown = useLastPresent(deleteTarget);
  const deleteTask = deleteShown ? taskById.get(deleteShown.taskId) : null;

  // The day being quick-added to and its button. The button toggles it; another day's moves it.
  const [quick, setQuick] = useState(null); // { date, anchor }
  const toggleQuick = (date, anchor) => {
    setMenu(null);
    setQuick((open) => (open?.date === date ? null : { date, anchor }));
  };
  const closeQuickRef = useRef(() => {});
  closeQuickRef.current = () => {
    quick?.anchor?.focus?.();
    setQuick(null);
  };
  const [stableCloseQuick] = useState(() => () => closeQuickRef.current());

  const closeMenu = useRef(() => {});
  closeMenu.current = () => {
    menu?.from?.focus?.();
    setMenu(null);
  };
  const [stableClose] = useState(() => () => closeMenu.current());

  const openMenu = (e, entry) => {
    e.preventDefault();
    // A keyboard's menu key has no pointer position, so it opens at the card.
    const box = e.currentTarget.getBoundingClientRect();
    const fromKeyboard = e.clientX === 0 && e.clientY === 0;
    setMenu({
      x: fromKeyboard ? box.left + 8 : e.clientX,
      y: fromKeyboard ? box.bottom : e.clientY,
      entry,
      from: e.currentTarget,
    });
  };

  const pick = (action) => {
    const { entry } = menu;
    setMenu(null);
    if (action === 'edit') onOpen(entry);
    else if (action === 'clone') onClone(entry);
    else if (action === 'delete') setDeleteTarget(entry);
    else {
      setMoveDate(entry.date);
      setMoveTarget(entry);
    }
  };

  const confirmMove = async () => {
    setMoving(true);
    await onMove(moveTarget, moveDate);
    setMoving(false);
    setMoveTarget(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    await onDelete(deleteTarget);
    setDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={({ active }) => {
        setMenu(null);
        setDragging(active.data.current?.entry || null);
      }}
      onDragCancel={() => setDragging(null)}
      onDragEnd={({ active, over }) => {
        setDragging(null);
        const entry = active.data.current?.entry;
        if (entry && over && over.id !== entry.date) onMove(entry, over.id);
      }}
    >
      <div className="ts-week" role="list" style={{ '--ts-week-days': days.length }}>
        {days.map((d) => (
          <DayColumn
            key={d}
            date={d}
            today={today}
            entries={entries.filter((e) => e.date === d)}
            taskById={taskById}
            editable={editable}
            flag={dayFlags?.get(d)}
            quickOpen={quick?.date === d}
            onQuick={toggleQuick}
            onOpen={onOpen}
            onMenu={openMenu}
            onDayClick={onDayClick}
          />
        ))}
      </div>

      {createPortal(
        <DragOverlay dropAnimation={null}>
          {dragging ? <EntryCard entry={dragging} taskById={taskById} movable overlay /> : null}
        </DragOverlay>,
        document.body
      )}

      {quick ? (
        <QuickAdd
          key={quick.date}
          date={quick.date}
          anchor={quick.anchor}
          tasks={tasks}
          config={config}
          onAdd={onQuickAdd}
          onClose={stableCloseQuick}
        />
      ) : null}

      {menu ? (
        <EntryMenu x={menu.x} y={menu.y} canChange={editable && !menu.entry.locked} onPick={pick} onClose={stableClose} />
      ) : null}

      <Modal open={Boolean(moveTarget)} onClose={moving ? undefined : () => setMoveTarget(null)} size="xs" overflow={false}>
        <Modal.Header closeDisabled={moving}>
          <Modal.Title>Move Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Move {moveShown ? formatHM(moveShown.durationSeconds) : ''} on{' '}
            <strong>{moveShown ? taskById.get(moveShown.taskId)?.path || 'this task' : ''}</strong> to:
          </p>
          <DatePicker
            block
            oneTap
            cleanable={false}
            format="MM/dd/yyyy"
            value={moveDate ? dateFromIso(moveDate) : null}
            onChange={(value) => setMoveDate(value ? isoFromDate(value) : null)}
            aria-label="Move to date"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setMoveTarget(null)} disabled={moving}>Cancel</Button>
          <Button variant="primary" onClick={confirmMove} loading={moving} disabled={!moveDate || moveDate === moveShown?.date}>
            {moveDate && moveDate !== moveShown?.date ? `Move to ${formatDate(moveDate, { weekday: 'short', month: 'short', day: 'numeric' })}` : 'Move'}
          </Button>
        </Modal.Footer>
      </Modal>

      <PeriodActionModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Time Entry"
        confirmLabel="Delete"
        confirmColor="red"
        showComment={false}
        loading={deleting}
      >
        {deleteShown ? (
          <p className="mb-0">
            Delete {formatHM(deleteShown.durationSeconds)} on <strong>{deleteTask?.path || 'this task'}</strong>
            {deleteShown.startTime && deleteShown.endTime
              ? ` (${formatClock12(deleteShown.startTime)} – ${formatClock12(deleteShown.endTime)})`
              : ''}
            {' '}on {formatDate(deleteShown.date, { weekday: 'short', month: 'short', day: 'numeric' })}? This cannot be undone.
          </p>
        ) : null}
      </PeriodActionModal>
    </DndContext>
  );
}
